import { Ionicons } from "@expo/vector-icons";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import type { StackNavigationProp } from '@react-navigation/stack';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect } from "react";
import { View, StyleSheet, ActivityIndicator, StatusBar } from "react-native";
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { DSText } from "../../../design-system/components/DSText";
import { spacing, radius } from "../../../design-system/tokens";
import { t } from '../../../i18n/translations';
import { RootStackParamList } from '../../../navigation/types';
import { supabase } from "../../../shared/services/supabaseService";
import { iqesColors, iqesGradients } from '../../../shared/ui/iqesDesignSystem';
import { useLanguageStore } from '../../quiz-core/languageStore';

const SinglePlayerSubmittingScreen: React.FC = () => {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<StackNavigationProp<RootStackParamList, 'SinglePlayerSubmittingScreen'>>();
  const route = useRoute<RouteProp<RootStackParamList, 'SinglePlayerSubmittingScreen'>>();
  const { resultsData } = route.params;
  const { language } = useLanguageStore();
  const correctCount = Number(resultsData?.correctCount ?? resultsData?.correctAnswers ?? resultsData?.score ?? 0);
  const totalQuestions = Number(resultsData?.totalQuestions ?? 0);
  const accuracy = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;

  useEffect(() => {
    let mounted = true;
    const submitResults = async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        const userId = userData?.user?.id;
        if (!userId) {
          console.error('[SinglePlayerSubmittingScreen] No authenticated user.');
          return;
        }
        const payload = {
          user_id: userId,
          category_id: Number(resultsData?.categoryId),
          difficulty_id: Number(resultsData?.difficulty_id),
          score: correctCount,
          total_questions: totalQuestions,
          accuracy: typeof resultsData?.accuracy === 'number' ? resultsData.accuracy : (totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0),
          answers: Array.isArray(resultsData?.answers) ? resultsData.answers : [],
          questions: Array.isArray(resultsData?.questions) ? resultsData.questions : [],
          language: resultsData?.language ?? 'en',
          time_taken_seconds: typeof resultsData?.timeTakenSeconds === 'number' && Number.isFinite(resultsData.timeTakenSeconds) ? Math.round(resultsData.timeTakenSeconds) : null,
          format_stats: resultsData?.formatStats ?? null,
          created_at: new Date().toISOString(),
        };
        const { error } = await supabase
          .from('singleplayer_results')
          .insert([payload]);
        if (error) {
          console.error('[SinglePlayerSubmittingScreen] Insert error:', error);
        }
      } catch (error) {
        console.error('Failed to submit results:', error);
      } finally {
        if (mounted) {
          navigation.replace("SinglePlayerResultsScreen", { resultsData });
        }
      }
    };
    submitResults();
    return () => {
      mounted = false;
    };
  }, [navigation, resultsData]);

  return (
    <LinearGradient
      colors={iqesGradients.appBackground}
      style={[styles.container, { paddingTop: Math.max(insets.top, spacing.lg) }]}
    >
      <StatusBar barStyle="light-content" backgroundColor={iqesColors.canvas} />
      <View style={styles.orbTop} pointerEvents="none" />
      <View style={styles.orbBottom} pointerEvents="none" />
      <View style={styles.sparkOne} pointerEvents="none" />
      <View style={styles.sparkTwo} pointerEvents="none" />

      <View style={styles.centered}>
        <LinearGradient
          colors={['rgba(13, 18, 38, 0.98)', 'rgba(11, 7, 27, 0.98)']}
          style={styles.completionCard}
        >
          <View style={styles.iconCircle}>
            <Ionicons name="sparkles" size={34} color={iqesColors.goldSoft} />
          </View>

          <DSText variant="caption" color={iqesColors.goldSoft} align="center" style={styles.eyebrow}>
            {t('results.sessionComplete', language) || 'SESSION COMPLETE'}
          </DSText>

          <DSText variant="heading" color={iqesColors.ivory} align="center" style={styles.title}>
            {t('results.quizComplete', language) || 'Quiz Complete'}
          </DSText>

          <DSText variant="body" color={iqesColors.mutedWarm} align="center" style={styles.subtitle}>
            {t('results.submittingYourResults', language) || 'Saving your progress...'}
          </DSText>

          <View style={styles.statsRow}>
            <View style={styles.statPill}>
              <DSText variant="caption" color={iqesColors.goldSoft} align="center" style={styles.statLabel}>
                {t('results.questionsExplored', language) || 'Questions'}
              </DSText>
              <DSText variant="title" color={iqesColors.ivory} align="center" style={styles.statValue}>
                {correctCount}/{totalQuestions}
              </DSText>
            </View>
            <View style={styles.statPill}>
              <DSText variant="caption" color={iqesColors.goldSoft} align="center" style={styles.statLabel}>
                {t('results.accuracy', language) || 'Accuracy'}
              </DSText>
              <DSText variant="title" color={iqesColors.ivory} align="center" style={styles.statValue}>
                {accuracy}%
              </DSText>
            </View>
          </View>

          <View style={styles.progressTrack}>
            <View style={[styles.progressFill, { width: `${Math.min(100, Math.max(8, accuracy))}%` }]} />
          </View>

          <View style={styles.savingRow}>
            <ActivityIndicator size="small" color={iqesColors.goldBright} />
            <DSText variant="label" color={iqesColors.goldSoft} align="center" style={styles.savingText}>
              {t('results.savedToJourney', language) || 'Saving to your Kingdom journey'}
            </DSText>
          </View>

          <View style={styles.scriptureCard}>
            <Ionicons name="book-outline" size={22} color={iqesColors.goldBright} />
            <DSText variant="body" color={iqesColors.ivory} align="center" style={styles.scriptureText}>
              {t('results.verseFightGoodFight', language) || '"I have fought the good fight, I have finished the race, I have kept the faith." — 2 Timothy 4:7'}
            </DSText>
          </View>
        </LinearGradient>
      </View>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: spacing.xl,
    paddingBottom: spacing['2xl'],
  },
  completionCard: {
    width: '100%',
    maxWidth: 460,
    borderRadius: radius['2xl'],
    borderWidth: 1.5,
    borderColor: 'rgba(246, 190, 58, 0.52)',
    padding: spacing['2xl'],
    alignItems: 'center',
    shadowColor: iqesColors.gold,
    shadowOpacity: 0.28,
    shadowRadius: 22,
    shadowOffset: { width: 0, height: 14 },
    elevation: 12,
  },
  iconCircle: {
    width: 88,
    height: 88,
    borderRadius: 44,
    backgroundColor: 'rgba(246, 190, 58, 0.16)',
    borderWidth: 2,
    borderColor: 'rgba(246, 190, 58, 0.72)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.lg,
  },
  eyebrow: {
    letterSpacing: 2.4,
    textTransform: 'uppercase',
    marginBottom: spacing.sm,
  },
  title: {
    marginBottom: spacing.sm,
    textShadowColor: 'rgba(246, 190, 58, 0.35)',
    textShadowRadius: 12,
  },
  subtitle: {
    marginBottom: spacing.lg,
    maxWidth: 320,
  },
  statsRow: {
    flexDirection: 'row',
    gap: spacing.md,
    width: '100%',
    marginVertical: spacing.md,
  },
  statPill: {
    flex: 1,
    minHeight: 92,
    borderRadius: radius.xl,
    borderWidth: 1,
    borderColor: 'rgba(246, 190, 58, 0.42)',
    backgroundColor: 'rgba(255, 248, 232, 0.07)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.md,
  },
  statLabel: {
    marginBottom: spacing.xs,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  statValue: {
    fontWeight: '900',
  },
  progressTrack: {
    width: '100%',
    height: 9,
    borderRadius: 999,
    backgroundColor: 'rgba(255, 248, 232, 0.12)',
    overflow: 'hidden',
    marginTop: spacing.sm,
    marginBottom: spacing.lg,
  },
  progressFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: iqesColors.goldBright,
  },
  savingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  savingText: {
    textTransform: 'uppercase',
    letterSpacing: 1.2,
  },
  scriptureCard: {
    width: '100%',
    gap: spacing.sm,
    backgroundColor: 'rgba(18, 11, 4, 0.52)',
    borderColor: 'rgba(246, 190, 58, 0.38)',
    borderWidth: 1,
    borderRadius: radius.xl,
    padding: spacing.lg,
    alignItems: "center",
  },
  scriptureText: {
    fontStyle: "italic",
    lineHeight: 25,
  },
  orbTop: {
    position: 'absolute',
    top: -80,
    right: -90,
    width: 240,
    height: 240,
    borderRadius: 120,
    backgroundColor: 'rgba(139, 92, 246, 0.23)',
  },
  orbBottom: {
    position: 'absolute',
    bottom: -90,
    left: -110,
    width: 260,
    height: 260,
    borderRadius: 130,
    backgroundColor: 'rgba(246, 190, 58, 0.12)',
  },
  sparkOne: {
    position: 'absolute',
    top: '23%',
    left: '18%',
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: iqesColors.goldSoft,
    opacity: 0.7,
  },
  sparkTwo: {
    position: 'absolute',
    bottom: '29%',
    right: '22%',
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: iqesColors.goldSoft,
    opacity: 0.58,
  },
});

export default SinglePlayerSubmittingScreen;
